import { Chip, lighten, SxProps, Tooltip } from '@mui/material';
import { blue } from '@mui/material/colors';
import React, { Dispatch, MouseEvent, ReactElement, SetStateAction, useMemo } from 'react';
import { Field } from 'sr-types/lib/person/v1/graphql';
import { useShallowMergeSxProps } from '../../../common/hooks/useShallowMergeSxProps';
import ResponsiveLayout from '../../../common/ResponsiveLayout';
import { useStyling } from '../../../common/Theme';

export type ChipsPreviewProps = {
    customFields: Field[];
    onViewMore?: () => void;
    unwrapSkillsContainer?: boolean;
    size?: 'small' | 'medium';
    setLastElementToFitIndex?: Dispatch<SetStateAction<number | null>>;
    customMoreElement?: (handleClick?: (event: MouseEvent<HTMLButtonElement>) => void) => ReactElement;
    hideMoreElement?: boolean;
    lastElementToFitIndex?: number | null;
    sx?: SxProps;
    chipSx?: SxProps;
    tileWidth?: number;
};
export default function ChipsPreview({
    customFields,
    onViewMore,
    unwrapSkillsContainer = false,
    size,
    setLastElementToFitIndex,
    customMoreElement,
    hideMoreElement = false,
    lastElementToFitIndex,
    sx = {},
    chipSx,
    tileWidth: tileWidthProp
}: ChipsPreviewProps) {
    const { isMobile } = useStyling();

    const attributesValues = useMemo(() => {
        return customFields.flatMap((attribute) => (Array.isArray(attribute.value) ? attribute.value : []));
    }, [customFields]);

    const mergedSx = useShallowMergeSxProps(
        {
            width: '100%',
            flexWrap: !unwrapSkillsContainer || isMobile ? 'wrap' : 'nowrap'
        },
        sx
    );

    return (
        <ResponsiveLayout
            gap={1}
            tileWidth={tileWidthProp ?? (isMobile || !unwrapSkillsContainer ? 110 : 140)}
            sx={mergedSx}
            onViewMore={onViewMore}
            setLastElementToFitIndex={setLastElementToFitIndex}
            hideMoreElement={hideMoreElement}
            customMoreElement={customMoreElement}
        >
            {attributesValues?.map((attribute, index) => {
                const isEllipsis = lastElementToFitIndex !== null && index === lastElementToFitIndex;
                return (
                    <Tooltip title={attribute} key={index}>
                        <Chip
                            label={attribute}
                            className={isEllipsis ? 'ellipsis' : undefined}
                            size={size || 'medium'}
                            sx={
                                chipSx ??
                                ((theme) => ({
                                    fontWeight: theme.typography.fontWeightMedium,
                                    backgroundColor: lighten(blue[100], 0.5),
                                    color: theme.palette.mode === 'dark' ? 'common.black' : 'text.secondary'
                                }))
                            }
                        />
                    </Tooltip>
                );
            })}
        </ResponsiveLayout>
    );
}
